import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Activity,
  Users,
  Clock,
  Plus,
  Minus,
  Share2,
  MessageSquare,
  Star,
  Folder,
  Info,
  TrendingUp,
  Download,
} from 'lucide-react';
import { api, handleApiError } from '@/services/client';
import { useAuthStore } from '@/stores/authStore';

interface ActivityItem {
  id: string;
  type: 'tab_created' | 'tab_closed' | 'workspace_joined' | 'app_generated' | 'session_started' | 'comment_added' | 'tab_shared' | 'bookmark_created';
  userId: string;
  userName: string;
  timestamp: string;
  details: string;
  metadata?: {
    tabId?: string;
    workspaceId?: string;
    appId?: string;
    action?: string;
  };
}

interface ActivityStats {
  tabs_created: number;
  apps_generated: number;
  items_shared: number;
  total_events: number;
}

interface PresenceUser {
  id: string;
  name: string;
  avatar?: string;
  status: string;
  viewing?: string;
}

interface ActivityFilters {
  type: 'all' | 'tabs' | 'workspaces' | 'apps' | 'collaboration';
  timeRange: 'today' | 'week' | 'month' | 'all';
}

function mapBackendActivity(raw: Record<string, unknown>): ActivityItem {
  const resourceType = String(raw.resource_type ?? '');
  let type = String(raw.type ?? 'session_started') as ActivityItem['type'];
  if (type === 'session_started' && resourceType === 'tab') {
    type = 'tab_created';
  }

  return {
    id: String(raw.id ?? crypto.randomUUID()),
    type,
    userId: String(raw.user_id ?? 'unknown'),
    userName: String(raw.user_name ?? 'You'),
    timestamp: String(raw.timestamp ?? new Date().toISOString()),
    details: String(raw.details ?? raw.description ?? ''),
    metadata: {
      tabId: resourceType === 'tab' ? String(raw.resource_id ?? '') : undefined,
      workspaceId: resourceType === 'workspace' ? String(raw.resource_id ?? '') : undefined,
      appId: resourceType === 'app' ? String(raw.resource_id ?? '') : undefined,
      action: String(raw.action ?? ''),
    },
  };
}

export const ActivityFeedPage = () => {
  const navigate = useNavigate();
  const authUser = useAuthStore((s) => s.user);
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [stats, setStats] = useState<ActivityStats>({
    tabs_created: 0,
    apps_generated: 0,
    items_shared: 0,
    total_events: 0,
  });
  const [activeUsers, setActiveUsers] = useState<PresenceUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<ActivityFilters>({
    type: 'all',
    timeRange: 'today',
  });

  const displayName = authUser?.full_name || authUser?.name || authUser?.email?.split('@')[0] || 'You';

  const fetchPresenceUsers = useCallback(async () => {
    try {
      const response = await api.get<{ users: PresenceUser[] }>('/presence/users');
      const users = response.data?.users ?? [];
      if (users.length > 0) {
        setActiveUsers(users.filter((u) => u.status === 'online' || u.status === 'away'));
        return;
      }
    } catch {
      // fall through to current user
    }
    setActiveUsers([{ id: 'self', name: displayName, status: 'online' }]);
  }, [displayName]);

  const fetchActivityFeed = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [feedRes, statsRes] = await Promise.all([
        api.get<{ activities: Record<string, unknown>[] }>('/activity', {
          params: { time_range: filters.timeRange },
        }),
        api.get<ActivityStats>('/activity/stats', {
          params: { time_range: filters.timeRange },
        }),
      ]);

      const rows = feedRes.data?.activities ?? [];
      setActivities(Array.isArray(rows) ? rows.map((r) => mapBackendActivity(r)) : []);
      setStats(statsRes.data ?? { tabs_created: 0, apps_generated: 0, items_shared: 0, total_events: 0 });
    } catch (err) {
      setError(handleApiError(err));
      setActivities([]);
      setStats({ tabs_created: 0, apps_generated: 0, items_shared: 0, total_events: 0 });
    } finally {
      setLoading(false);
    }
  }, [filters.timeRange]);

  useEffect(() => {
    fetchActivityFeed();
    fetchPresenceUsers();
  }, [fetchActivityFeed, fetchPresenceUsers]);

  const exportReport = async () => {
    try {
      const res = await api.get('/activity/export', {
        params: { time_range: filters.timeRange },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `khiimori-activity-${filters.timeRange}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(handleApiError(err));
    }
  };

  const getActivityIcon = (type: ActivityItem['type']) => {
    switch (type) {
      case 'tab_created': return <Plus className="w-5 h-5" />;
      case 'tab_closed': return <Minus className="w-5 h-5" />;
      case 'workspace_joined': return <Users className="w-5 h-5" />;
      case 'app_generated': return <Star className="w-5 h-5" />;
      case 'session_started': return <Activity className="w-5 h-5" />;
      case 'comment_added': return <MessageSquare className="w-5 h-5" />;
      case 'tab_shared': return <Share2 className="w-5 h-5" />;
      case 'bookmark_created': return <Folder className="w-5 h-5" />;
      default: return <Info className="w-5 h-5" />;
    }
  };

  const getActivityColor = (type: ActivityItem['type']) => {
    switch (type) {
      case 'tab_created': return 'from-green-500 to-emerald-600';
      case 'tab_closed': return 'from-red-500 to-rose-600';
      case 'workspace_joined': return 'from-blue-500 to-indigo-600';
      case 'app_generated': return 'from-purple-500 to-violet-600';
      case 'session_started': return 'from-orange-500 to-amber-600';
      case 'comment_added': return 'from-cyan-500 to-teal-600';
      case 'tab_shared': return 'from-pink-500 to-rose-600';
      case 'bookmark_created': return 'from-yellow-500 to-orange-600';
      default: return 'from-khiimori-bg-tertiary to-khiimori-bg-tertiary';
    }
  };

  const formatTimeAgo = (timestamp: string) => {
    const now = new Date();
    const past = new Date(timestamp);
    const diffMs = now.getTime() - past.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return past.toLocaleDateString();
  };

  const filteredActivities = activities.filter((activity) => {
    if (filters.type === 'all') return true;
    const typeMap: Record<string, string[]> = {
      tabs: ['tab_created', 'tab_closed'],
      workspaces: ['workspace_joined', 'session_started'],
      apps: ['app_generated'],
      collaboration: ['comment_added', 'tab_shared'],
    };
    return typeMap[filters.type]?.includes(activity.type) ?? false;
  });

  const onlineCount = activeUsers.filter((u) => u.status === 'online').length || 1;

  return (
    <div className="max-w-7xl mx-auto space-y-6">
        {error && (
          <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm text-red-300">
            {error}
          </div>
        )}

        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card p-8"
        >
          <div className="flex items-center justify-between">
            <div className="flex gap-4">
              <div className="px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-xl flex items-center gap-2">
                <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                <span className="text-sm font-medium text-emerald-400">{onlineCount} Online</span>
              </div>
              <div className="px-4 py-2 bg-blue-500/10 border border-blue-500/20 rounded-xl flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-blue-400" />
                <span className="text-sm font-medium text-blue-400">{stats.total_events} Events</span>
              </div>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-4">
            <div className="flex flex-wrap gap-2">
              {(['all', 'tabs', 'workspaces', 'apps', 'collaboration'] as const).map((type) => (
                <motion.button
                  key={type}
                  type="button"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setFilters({ ...filters, type })}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    filters.type === type
                      ? 'bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white shadow-lg'
                      : 'bg-khiimori-bg-elevated/50 text-khiimori-text-secondary hover:bg-khiimori-bg-elevated/80 border border-khiimori-border'
                  }`}
                >
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </motion.button>
              ))}
            </div>

            <div className="flex flex-wrap gap-2 ml-auto">
              {(['today', 'week', 'month', 'all'] as const).map((range) => (
                <button
                  key={range}
                  type="button"
                  onClick={() => setFilters({ ...filters, timeRange: range })}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    filters.timeRange === range
                      ? 'bg-violet-500/20 text-violet-400 border border-violet-500/30'
                      : 'bg-khiimori-bg-elevated/50 text-khiimori-text-secondary border border-khiimori-border hover:bg-khiimori-bg-elevated/80'
                  }`}
                >
                  <Clock className="w-4 h-4 inline-block mr-1" />
                  {range.charAt(0).toUpperCase() + range.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <AnimatePresence mode="popLayout">
              {loading ? (
                <div className="glass-card p-12 text-center">
                  <Activity className="w-12 h-12 text-khiimori-text-muted mx-auto mb-4 animate-pulse" />
                  <p className="text-khiimori-text-muted">Loading activities...</p>
                </div>
              ) : filteredActivities.length === 0 ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="glass-card p-12 text-center"
                >
                  <Activity className="w-16 h-16 text-khiimori-text-muted mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-khiimori-text-primary mb-2">No Activities Yet</h3>
                  <p className="text-khiimori-text-muted">
                    Open tabs, generate apps, or join workspaces — activity will show up here.
                  </p>
                </motion.div>
              ) : (
                filteredActivities.map((activity, index) => (
                  <motion.div
                    key={activity.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ delay: index * 0.03 }}
                    className="glass-card p-6 hover:bg-khiimori-bg-elevated/80 transition-all"
                  >
                    <div className="flex items-start gap-4">
                      <div className={`p-3 bg-gradient-to-br ${getActivityColor(activity.type)} rounded-xl text-white flex-shrink-0`}>
                        {getActivityIcon(activity.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-semibold text-khiimori-text-primary">{activity.userName}</span>
                              <span className="text-xs px-2 py-1 bg-khiimori-bg-elevated/50 rounded-full text-khiimori-text-secondary">
                                {activity.type.replace(/_/g, ' ')}
                              </span>
                            </div>
                            <p className="text-khiimori-text-secondary">{activity.details}</p>
                          </div>
                          <span className="text-sm text-khiimori-text-muted flex-shrink-0">
                            {formatTimeAgo(activity.timestamp)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
            </AnimatePresence>
          </div>

          <div className="space-y-6">
            <div className="glass-card p-6">
              <h3 className="text-lg font-bold text-khiimori-text-primary mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-violet-400" />
                Today's Summary
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                  <div className="flex items-center gap-2">
                    <Plus className="w-4 h-4 text-emerald-400" />
                    <span className="text-sm text-emerald-300">Tabs Created</span>
                  </div>
                  <span className="font-bold text-emerald-400">{stats.tabs_created}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
                  <div className="flex items-center gap-2">
                    <Star className="w-4 h-4 text-blue-400" />
                    <span className="text-sm text-blue-300">Apps Generated</span>
                  </div>
                  <span className="font-bold text-blue-400">{stats.apps_generated}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-violet-500/10 rounded-lg border border-violet-500/20">
                  <div className="flex items-center gap-2">
                    <Share2 className="w-4 h-4 text-violet-400" />
                    <span className="text-sm text-violet-300">Items Shared</span>
                  </div>
                  <span className="font-bold text-violet-400">{stats.items_shared}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-orange-500/10 rounded-lg border border-orange-500/20">
                  <div className="flex items-center gap-2">
                    <Activity className="w-4 h-4 text-orange-400" />
                    <span className="text-sm text-orange-300">Total Events</span>
                  </div>
                  <span className="font-bold text-orange-400">{stats.total_events}</span>
                </div>
              </div>
            </div>

            <div className="glass-card p-6">
              <h3 className="text-lg font-bold text-khiimori-text-primary mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-400" />
                Active Users
              </h3>
              {activeUsers.length === 0 ? (
                <p className="text-sm text-khiimori-text-muted">No collaborators online right now.</p>
              ) : (
                <div className="space-y-3">
                  {activeUsers.map((user) => (
                    <div key={user.id} className="flex items-center gap-3 p-2 hover:bg-khiimori-bg-elevated/50 rounded-lg transition-colors">
                      <div className="w-10 h-10 bg-gradient-to-br from-violet-400 to-fuchsia-500 rounded-full flex items-center justify-center text-white font-semibold">
                        {user.avatar || user.name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-khiimori-text-primary truncate">{user.name}</div>
                        <div className="text-xs text-khiimori-text-muted capitalize">
                          {user.status === 'online' ? 'Active now' : user.status}
                          {user.viewing ? ` · ${user.viewing}` : ''}
                        </div>
                      </div>
                      {user.status === 'online' && <div className="w-2 h-2 bg-emerald-500 rounded-full shrink-0" />}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-gradient-to-br from-violet-500 to-fuchsia-500 rounded-2xl p-6 text-white">
              <h3 className="text-lg font-bold mb-3">Quick Actions</h3>
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => navigate('/home?section=activity')}
                  className="w-full px-4 py-2 bg-khiimori-bg-elevated/30 hover:bg-khiimori-bg-elevated/50 rounded-lg transition-colors text-left"
                >
                  View My Activity
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/sync')}
                  className="w-full px-4 py-2 bg-khiimori-bg-elevated/30 hover:bg-khiimori-bg-elevated/50 rounded-lg transition-colors text-left"
                >
                  Team Activity
                </button>
                <button
                  type="button"
                  onClick={() => void exportReport()}
                  className="w-full px-4 py-2 bg-khiimori-bg-elevated/30 hover:bg-khiimori-bg-elevated/50 rounded-lg transition-colors text-left flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Export Report
                </button>
              </div>
            </div>
          </div>
        </div>
    </div>
  );
};
